/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCall, createOperationCallAndPoll, OperationHandler } from 'src/core/services/api/createOperationCall';
import { fileService } from 'src/core/services/api/fileService';
import { httpService } from 'src/core/services/api/httpService';
import { OperationStatusBaseSwagger } from 'src/core/services/api/operationSwagger';
import { ServiceKey } from 'src/core/stores/AzureSessionStore';
import { AmlProjectMetadata, ImportLabelObject } from 'src/core/types/azureMachineLearningTypes';
import { ResourceListResults } from 'src/core/types/ResourceListTypes';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { CustomTextExportedProjectSwaggerOldVersion } from 'src/customText/core/services/projects/customTextProjectSwaggerOldVersion';
import {
    CustomTextCopyProjectSwagger,
    CustomTextExportedProjectSwagger,
    CustomTextProjectJobStatusSwagger,
    CustomTextProjectKindSwagger,
    CustomTextProjectSwagger
} from 'src/customText/core/services/projects/customTextProjectSwaggers';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';

/**
 * Represents a service to perform CRUD operations for custom text project.
 */
export const getCustomTextProjectService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction, webApiUrl } = getEnvironment({ apiName: 'customText' });

    const apiVersionOption = LuisConstants.CustomText.AuthoringApiVersion;

    const apiOptions = (serviceKey?: ServiceKey, options?: { isMergePatchType?: boolean }) =>
        luisRequestOptionsBuilder()
            .useDefaultOptions(serviceKey ?? context.serviceKey, isProduction)
            .useContentType(options?.isMergePatchType ? 'merge-patch+json' : 'json')
            .build();

    const getUrl = (projectName?: string, resource?: string, apiVersion?: string) => {
        const builder = urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .queryParam('api-version', apiVersion ?? LuisConstants.CustomText.AuthoringApiVersion)
            .queryParam('showAmlMetadata', 'true');

        if (resource) {
            builder.resource(resource);
        }

        return builder.build();
    };

    const authorizeCopyProject = async ({
        serviceKey,
        resourceId,
        projectName,
        projectKind,
        allowOverwrite,
        targetResourceEndpoint,
        storageInputContainerName
    }: {
        serviceKey: string;
        resourceId: string;
        projectName: string;
        allowOverwrite: boolean;
        targetResourceEndpoint: string;
        storageInputContainerName: string;
        projectKind: CustomTextProjectKind;
    }): Promise<CustomTextCopyProjectSwagger> => {
        const url = isProduction
            ? urlBuilder()
                  .baseUrl(`${targetResourceEndpoint}/language/authoring/analyze-text`)
                  .project(projectName)
                  .resource(':authorize-copy')
                  .queryParam('api-version', LuisConstants.CustomText.AuthoringApiVersion)
                  .queryParam('showAmlMetadata', 'true')
                  .build()
            : getUrl(projectName, ':authorize-copy', LuisConstants.CustomText.AuthoringApiVersion);
        const resourceKey = isProduction ? serviceKey : resourceId;
        const modifiedApiOptions = luisRequestOptionsBuilder()
            .useAuthoringKey(resourceKey, isProduction, resourceId)
            .build();

        const response = await httpService.post<CustomTextCopyProjectSwagger>(
            url,
            {
                projectKind,
                allowOverwrite,
                storageInputContainerName
            },
            modifiedApiOptions
        );

        return response.data;
    };

    const copyProject = async ({
        operationHandle,
        copyProjectSwagger
    }: {
        copyProjectSwagger: CustomTextCopyProjectSwagger;
        operationHandle: OperationHandler<CustomTextProjectJobStatusSwagger>;
    }): Promise<void> => {
        const url = getUrl(copyProjectSwagger.targetProjectName, ':copy', LuisConstants.CustomText.AuthoringApiVersion);

        const { headers: copyJobHeaders } = await httpService.post<void>(url, copyProjectSwagger, apiOptions());

        const jobUrl = (<Record<string, string>>copyJobHeaders)[LuisConstants.OperationHeaders.LocationHeader];

        await createOperationCall<CustomTextProjectJobStatusSwagger>(jobUrl, apiOptions(), operationHandle);
    };

    const getAmlWebApiUrl = (projectName?: string) => {
        const builder = urlBuilder()
            .baseUrl(webApiUrl)
            .resource('internal')
            .project(projectName)
            .resource('amlConnection')
            .queryParam('api-version', LuisConstants.CustomText.InternalApiVersion);

        return builder.build();
    };

    const createOrUpdateProject = async ({
        projectName,
        projectKind,
        multiLingual,
        description,
        language,
        settings,
        storageInputContainerName
    }: {
        projectName: string;
        language?: string;
        description?: string;
        multiLingual?: boolean;
        storageInputContainerName?: string;
        projectKind?: CustomTextProjectKindSwagger;
        settings?: { confidenceThreshold?: number };
    }): Promise<CustomTextProjectSwagger> => {
        const { data: createdProject } = await httpService.patch<CustomTextProjectSwagger>(
            getUrl(projectName),
            {
                projectName,
                projectKind,
                multiLingual,
                description,
                language,
                storageInputContainerName,
                settings,
                multilingual: multiLingual
            },
            apiOptions(undefined, { isMergePatchType: true })
        );

        return createdProject;
    };

    const getProjects = async (nextLink?: string, serviceKey?: ServiceKey): Promise<ResourceListResults<CustomTextProjectSwagger>> => {
        const url = nextLink ?? getUrl();

        const { data } = await httpService.get<ResourceListResults<CustomTextProjectSwagger>>(url, apiOptions(serviceKey));

        return data;
    };

    const getProject = async ({ projectName }: { projectName: string }): Promise<CustomTextProjectSwagger> => {
        const { data: project } = await httpService.get<CustomTextProjectSwagger>(getUrl(projectName), apiOptions());

        return project;
    };

    const linkProjectToAml = async ({
        projectName,
        amlProjectMetadata
    }: {
        projectName: string;
        amlProjectMetadata: AmlProjectMetadata;
    }) => {
        try {
            await httpService.patch(getAmlWebApiUrl(projectName), amlProjectMetadata, apiOptions(undefined, { isMergePatchType: true }));
        } catch (error) {
            throw error;
        }
    };

    const terminateAmlConnection = async ({ projectName }: { projectName: string }) => {
        try {
            await httpService.delete(getAmlWebApiUrl(projectName), apiOptions(undefined, { isMergePatchType: true }));
        } catch (error) {
            throw error;
        }
    };

    const importAmlLabels = async ({ projectName, importLabelObject }: { projectName: string; importLabelObject: ImportLabelObject }) => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .resource('internal')
            .project(projectName)
            .resource('amlConnection')
            .resource(':import')
            .queryParam('api-version', LuisConstants.CustomText.InternalApiVersion)
            .queryParam('isLabelingLocked', 'true')
            .build();
        try {
            const { data } = await createOperationCallAndPoll(
                httpService.post(url, importLabelObject, apiOptions()),
                apiOptions(),
                undefined,
                response => (<Record<string, string>>response.headers)[LuisConstants.OperationHeaders.LocationHeader]
            );
            return data;
        } catch (error) {
            throw error;
        }
    };

    const deleteProject = async ({
        projectName,
        operationHandle
    }: {
        projectName: string;
        operationHandle: OperationHandler<CustomTextProjectJobStatusSwagger>;
    }): Promise<void> => {
        const { headers: deleteJobHeaders } = await httpService.delete<void>(getUrl(projectName), apiOptions());

        const jobUrl = (<Record<string, string>>deleteJobHeaders)[LuisConstants.OperationHeaders.LocationHeader];
        await createOperationCall<CustomTextProjectJobStatusSwagger>(jobUrl, apiOptions(), operationHandle);
    };

    const initiateExportProject = async ({
        projectName,
        showAutoTag = false,
        assetsToExport,
        trainedModelName
    }: {
        projectName: string;
        showAutoTag?: boolean;
        assetsToExport: ('all' | 'classifiers' | 'extractors' | 'documents')[];
        trainedModelName?: string;
    }): Promise<string> => {
        const urlBuild = urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .resource(':export')
            .queryParam('api-version', apiVersionOption)
            .queryParam('stringIndexType', 'Utf16CodeUnit');

        if (showAutoTag) {
            urlBuild.queryParam('showAutoTag', 'true');
        }

        if (trainedModelName) {
            urlBuild.queryParam('trainedModelLabel', trainedModelName);
        }

        const url = urlBuild.build();

        const payload = {
            assetsToExport: assetsToExport.some(a => a === 'all') ? ['*'] : assetsToExport
        };

        const options = luisRequestOptionsBuilder()
            .useDefaultOptions(context.serviceKey, isProduction)
            .useCustomHeader('format', 'JSON')
            .build();

        const { headers } = await httpService.post<void>(url, payload, options);

        return (<Record<string, string>>headers)[LuisConstants.OperationHeaders.CTLocationHeader];
    };

    const pollExportProject = async ({
        statusUrl,
        operationHandler
    }: {
        statusUrl: string;
        operationHandler: OperationHandler<CustomTextProjectJobStatusSwagger>;
    }): Promise<void> => {
        await createOperationCall(statusUrl, apiOptions(), operationHandler);
    };

    const getExportProjectResults = async ({ resultUrl }: { resultUrl: string }): Promise<CustomTextExportedProjectSwagger> => {
        const { data: exportedProject } = await httpService.get<CustomTextExportedProjectSwagger>(resultUrl, apiOptions());

        return exportedProject;
    };

    const initiateAndPollImportProject = async ({
        projectName,
        projectToImport,
        operationHandler,
        apiVersion = LuisConstants.CustomText.AuthoringApiVersion
    }: {
        apiVersion?: string;
        projectName: string;
        projectToImport: CustomTextExportedProjectSwagger | CustomTextExportedProjectSwaggerOldVersion;
        operationHandler: OperationHandler<CustomTextProjectJobStatusSwagger>;
    }): Promise<void> => {
        const url = getUrl(projectName, ':import');

        const options = luisRequestOptionsBuilder()
            .useDefaultOptions(context.serviceKey, isProduction)
            .useCustomHeader('format', 'JSON')
            .build();

        const { headers } = await httpService.post<void>(url, { ...projectToImport, projectFileVersion: apiVersion }, options);

        const statusUrl = (<Record<string, string>>headers)[LuisConstants.OperationHeaders.CTLocationHeader];

        await createOperationCall(statusUrl, apiOptions(), operationHandler);
    };

    const exportProject = async ({ projectName, trainedModelName }: { projectName: string; trainedModelName?: string }) => {
        let urlBuild = urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .resource(':export')
            .queryParam('api-version', apiVersionOption)
            .queryParam('stringIndexType', 'Utf16CodeUnit');

        if (trainedModelName) {
            urlBuild = urlBuild.queryParam('trainedModelLabel', trainedModelName);
        }
        const url = urlBuild.build();

        const options = luisRequestOptionsBuilder()
            .useDefaultOptions(context.serviceKey, isProduction)
            .useCustomHeader('format', 'JSON')
            .build();

        const { data } = await createOperationCallAndPoll<OperationStatusBaseSwagger & { resultUrl: string }>(
            httpService.post(url, { assets: ['*'] }, options),
            apiOptions(),
            undefined,
            response => (<Record<string, string>>response.headers)[LuisConstants.OperationHeaders.LocationHeader]
        );

        const { data: projectData } = await httpService.get<CustomTextProjectSwagger>(data.resultUrl, apiOptions());

        const fileName = trainedModelName ? `${projectName}(${trainedModelName}).json` : `${projectName}.json`;
        fileService.saveToDisk(JSON.stringify(projectData, null, 4), fileName);
    };

    return {
        getProject,
        copyProject,
        getProjects,
        deleteProject,
        exportProject,
        importAmlLabels,
        linkProjectToAml,
        pollExportProject,
        authorizeCopyProject,
        initiateExportProject,
        createOrUpdateProject,
        terminateAmlConnection,
        getExportProjectResults,
        initiateAndPollImportProject
    };
});
